import { useEffect, useMemo, useState } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { EventType } from "@/services/api/events";
import PencilIcon from "@/components/SVGComponents/PencilIcon";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import Table from "@/components/Table/Table";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import { TableCellTooltip } from "@/components/Tooltip";
import type { Column } from "@/types/common";
import venueService from "@/services/api/venues";
import type { Venue } from "@/services/api/venues";
import toast from "@/lib/toast";
import { PAGINATION } from "@/lib/constants";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { formatINR } from "@/lib/currency";
import { formatDate } from "@/lib/dateFormat";
import { getVenues } from "@/store/slices/venueSlice";
import Button from "@/components/Button/Button";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import LocationPinIcon from "@/components/SVGComponents/LocationPinIcon";
import CalendarIcon from "@/components/SVGComponents/CalendarIcon";
import Pagination from "@/components/Table/Pagination";
import AddVenueModal from "../AddVenueModal/AddVenueModal";
import styles from "./VenuesSection.module.scss";

// GET /admin/venues supports the same pageNo/perPage params as every other
// list endpoint (see services/api/venues.ts, venueSlice.ts's totalVenues
// from the response's totalRecord) — real server-side pagination, one
// page of rows fetched at a time, same convention as Members.tsx/
// PastEvents.tsx rather than the old "fetch everything" approach.
const ROWS_PER_PAGE = PAGINATION.DEFAULT_ROWS_PER_PAGE;

// `EventType`'s three values (see services/api/events.ts) — falls back to
// title-casing any unseen value rather than hiding it, defensive in case
// venues' `suitableFor` ever diverges from that confirmed vocabulary.
const SUITABLE_FOR_LABEL: Record<string, string> = {
  MARQUEE: "Marquee",
  MASTERCLASS: "Masterclass",
  MICRO_MEETUPS: "Micro-meetups",
};

const formatSuitableFor = (value: string) =>
  SUITABLE_FOR_LABEL[value] ?? value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, " ");

const USED_FOR_OPTIONS = Object.entries(SUITABLE_FOR_LABEL).map(([value, label]) => ({ label, value }));

// "Last used" filter — value is a day count, turned into a
// `lastUsedFromDate` (start of that day, ISO) when fetching.
const LAST_USED_OPTIONS = [
  { label: "Last 7 days", value: "7" },
  { label: "Last 30 days", value: "30" },
  { label: "Last 90 days", value: "90" },
  { label: "Last year", value: "365" },
];

const getFromDate = (days: string | null) => {
  if (!days) return undefined;
  const date = new Date();
  date.setDate(date.getDate() - Number(days));
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
};

interface VenueFilters {
  city: string | null;
  usedFor: string | null;
  lastUsed: string | null;
}

const EMPTY_FILTERS: VenueFilters = { city: null, usedFor: null, lastUsed: null };

const BASE_COLUMNS: Column<Venue>[] = [
  {
    name: "Venue",
    grow: 0,
    width: "22%",
    cell: (row) => <TableCellTooltip value={row?.name || "—"} textClassName={styles.nameCell} />,
  },
  { name: "City",grow: 0, width: "18%", selector: (row) => row?.chapterName || "—" },
  {
    name: "Used for",
    grow: 0,
    width: "28%",
    cell: (row) => (
      <TableCellTooltip
        value={(row?.suitableFor ?? []).map(formatSuitableFor).join(" · ")}
        textClassName={styles.usedForCell}
      />
    ),
  },
  {
    name: "Last used",
    grow: 0,
    width: "15%",
    selector: (row) => formatDate(row?.lastUsedAt, { locale: navigator.language, day: "numeric", month: "numeric", year: "numeric" }),
  },
];

const VenuesSection = () => {
  const dispatch = useAppDispatch();
  const { venues, totalVenues, isFetching } = useAppSelector((state) => state.venue);
  // Chapters are already fetched by the parent CitiesChapters.tsx — reused
  // here for the "City" filter's options.
  const { chapters } = useAppSelector((state) => state.chapter);
  const [pageSelected, setPageSelected] = useState(PAGINATION.DEFAULT_PAGE_NUMBER);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [filters, setFilters] = useState<VenueFilters>(EMPTY_FILTERS);
  const [isAddVenueModalOpen, setIsAddVenueModalOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Venue | null>(null);

  const formConfig = useMemo(
    () => ({
      city: {
        controlType: "select" as const,
        label: "City",
        isControlled: true,
        props: { options: chapters.map((chapter) => ({ label: chapter.name, value: String(chapter.id) })) },
      },
      usedFor: {
        controlType: "select" as const,
        label: "Used for",
        isControlled: true,
        props: { options: USED_FOR_OPTIONS },
      },
      lastUsed: {
        controlType: "select" as const,
        label: "Last used",
        isControlled: true,
        props: { options: LAST_USED_OPTIONS },
      },
    }),
    [chapters],
  );

  const fetchVenues = (pageNo: number) =>
    dispatch(
      getVenues({
        pageNo,
        perPage: ROWS_PER_PAGE,
        search: debouncedSearch || undefined,
        cityId: filters.city ? Number(filters.city) : undefined,
        eventType: (filters.usedFor as EventType | null) ?? undefined,
        lastUsedFromDate: getFromDate(filters.lastUsed),
      }),
    );

  useEffect(() => {
    fetchVenues(pageSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageSelected, debouncedSearch, filters]);

  const closeAddModal = () => {
    setIsAddVenueModalOpen(false);
    setEditingVenue(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await venueService.deleteVenue(deleteTarget.id);
      toast.success("Venue deleted.");
      // Deleting the last row on a page beyond the first leaves it empty —
      // step back a page (which re-fetches via the effect above) instead of
      // showing a blank page; otherwise just re-fetch the current page.
      if (venues.length === 1 && pageSelected > 1) {
        setPageSelected(pageSelected - 1);
      } else {
        fetchVenues(pageSelected);
      }
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  };

  const getRowActions = (venue: Venue): RowAction[] => [
    {
      key: "edit",
      label: "Edit",
      icon: <PencilIcon />,
      onClick: () => setEditingVenue(venue),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <CrossIcon withBg={false} stroke="currentColor" strokeWidth={1.5} width={14} height={14} />,
      tone: "danger",
      dividerBefore: true,
      onClick: () => setDeleteTarget(venue),
    },
  ];

  const columns: Column<Venue>[] = [
    ...BASE_COLUMNS,
    {
      name: "Action",
      width: "6%",
      grow: 0,
      cell: (row) => <RowActionsMenu actions={getRowActions(row)} ariaLabel={`Actions for ${row.name}`} />,
    },
  ];

  return (
    <>
      {/* Desktop Table View */}
      <div className={styles.desktopView}>
        <Table<Venue>
          columns={columns}
          data={venues}
          progressPending={isFetching}
          // A fixed, modest height rather than the near-full-viewport
          // `calc(100vh - 210px)` Members.tsx/Support.tsx/TeamRoles.tsx use —
          // same reasoning as PastEvents.tsx's own `containerHeight="560px"`:
          // this table sits below the Chapters grid within CitiesChapters.tsx,
          // which needs to stay in normal page flow rather than getting
          // pushed off past a table tall enough to fill the screen.
          containerHeight="480px"
          header={{
            titleName: "Venues",
            search,
            // A new search starts back on page 1.
            setSearch: (value) => {
              setSearch(value);
              setPageSelected(PAGINATION.DEFAULT_PAGE_NUMBER);
            },
            searchPlaceholder: "Search venue",
            formConfig,
            onFilterSubmit: (data) => {
              setFilters({
                city: data.city ?? null,
                usedFor: data.usedFor ?? null,
                lastUsed: data.lastUsed ?? null,
              });
              setPageSelected(PAGINATION.DEFAULT_PAGE_NUMBER);
            },
            hideExport: true,
            addButtonTitle: "ADD VENUE",
            onAddClick: () => setIsAddVenueModalOpen(true),
          }}
          paginationProps={{
            isPagination: true,
            pageSelected,
            totalCount: totalVenues,
            rowsPerPageValue: ROWS_PER_PAGE,
            setPageSelected,
          }}
        />
      </div>

      {/* Mobile Card View */}
      <div className={styles.mobileView}>
        <div className={styles.mobileHeader}>
          <div className={styles.mobileTitleGroup}>
            <h3 className={styles.mobileTitle}>Venues</h3>
            {totalVenues > 0 && <span className={styles.mobileCountBadge}>{totalVenues}</span>}
          </div>
          <Button
            className={styles.mobileAddBtn}
            onClick={() => setIsAddVenueModalOpen(true)}
          >
            <PlusIcon width={13} height={13} />
            <span>ADD VENUE</span>
          </Button>
        </div>

        <div className={styles.mobileCardList}>
          {isFetching ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className={styles.mobileSkeletonCard}>
                <div className={styles.skeletonHeader}>
                  <div className={styles.skeletonVenueGroup}>
                    <div className={styles.skeletonLineTitle} />
                    <div className={styles.skeletonCityBadge} />
                  </div>
                  <div className={styles.skeletonActionBtn} />
                </div>
                <div className={styles.skeletonTagsRow}>
                  <div className={styles.skeletonChip} />
                  <div className={styles.skeletonChip} />
                </div>
                <div className={styles.skeletonDivider} />
                <div className={styles.skeletonFooter}>
                  <div className={styles.skeletonLineTiny} />
                </div>
              </div>
            ))
          ) : venues.length === 0 ? (
            <div className={styles.mobileEmptyState}>
              <div className={styles.emptyIconCircle}>
                <LocationPinIcon width={22} height={22} />
              </div>
              <h4 className={styles.mobileEmptyTitle}>No venues found</h4>
              <p className={styles.mobileEmptyCaption}>No venues have been added yet.</p>
            </div>
          ) : (
            venues.map((venue) => (
              <div key={venue.id} className={styles.mobileVenueCard}>
                <div className={styles.mobileCardHeader}>
                  <div className={styles.mobileVenueNameGroup}>
                    <h4 className={styles.mobileVenueName} title={venue.name || undefined}>
                      {venue.name || "—"}
                    </h4>
                    {venue.chapterName && (
                      <div className={styles.mobileCityBadge}>
                        <LocationPinIcon className={styles.mobilePinIcon} />
                        <span className={styles.mobileCityName}>{venue.chapterName}</span>
                      </div>
                    )}
                  </div>
                  <RowActionsMenu
                    actions={getRowActions(venue)}
                    ariaLabel={`Actions for ${venue.name}`}
                    triggerClassName={styles.mobileActionTrigger}
                  />
                </div>

                {venue.suitableFor && venue.suitableFor.length > 0 && (
                  <div className={styles.mobileSuitableSection}>
                    <span className={styles.mobileSectionLabel}>Suitable For</span>
                    <div className={styles.mobileSuitableRow}>
                      {venue.suitableFor.map((tag) => (
                        <span key={tag} className={styles.mobileSuitableChip}>
                          {formatSuitableFor(tag)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className={styles.mobileCardFooter}>
                  <div className={styles.mobileLastUsedGroup}>
                    <CalendarIcon className={styles.mobileCalendarIcon} />
                    <span className={styles.mobileLastUsedLabel}>Last used</span>
                    <span className={styles.mobileLastUsedValue}>
                      {venue.lastUsedAt ? new Date(venue.lastUsedAt).toLocaleDateString() : "Never used"}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {totalVenues > ROWS_PER_PAGE && (
          <div className={styles.mobilePaginationContainer}>
            <Pagination
              totalCount={totalVenues}
              pageSelected={pageSelected}
              rowsPerPageValue={ROWS_PER_PAGE}
              setPageSelected={setPageSelected}
            />
          </div>
        )}
      </div>

      <AddVenueModal
        isOpen={isAddVenueModalOpen || editingVenue !== null}
        venueToEdit={editingVenue}
        onClose={closeAddModal}
        onSaved={() => fetchVenues(pageSelected)}
      />

      <ConfirmDeleteModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Venue"
        message={
          <>
            Are you sure you want to delete <strong>{deleteTarget?.name || "this venue"}</strong>? This action cannot
            be undone.
          </>
        }
      />
    </>
  );
};

export default VenuesSection;
